/**
 * AGP — bespoke Testimonials page (2026 redesign handoff, Page 4): page hero,
 * intro, a hairline 3-up grid of Playfair pull-quotes, and a closing CTA band.
 *
 * All the page copy comes from the Site Pages manifest (admin → Storefront →
 * Site Pages → Testimonials); the defaults are the handoff copy verbatim, so an
 * un-edited page renders exactly as it did before the manifest existed.
 *
 * The QUOTE LIST has two possible sources — see `resolveAgpQuotes`.
 */
import { Link } from 'react-router-dom';
import { AGP_SITE, type CompanyTestimonial } from '@mlm/shared';
import { useStorefrontContent } from '../../../storefront/lib/hooks';
import { renderLines } from '../../../storefront/lib/rich-paras';
import { useSiteContent, type SiteContent } from '../../../storefront/lib/site-content';
import { AgpShell } from './shell';

const QUOTES_PATH = 'testimonials.quotes.items';

/** One rendered testimonial, normalized from whichever source supplied it. */
export type AgpQuote = { quote: string; author: string; role?: string; photo?: string };

/**
 * Which list of quotes the page shows:
 *
 *   1. the Site Pages repeater, once an admin has CUSTOMIZED it
 *   2. else storefront `company.testimonials` (Storefront → Pages → About),
 *      where AGP's quotes were editable before this page had its own CMS
 *   3. else the manifest defaults (the handoff's six)
 *
 * Step 2 is what keeps this backwards-compatible: the new repeater is inert
 * until someone explicitly clicks Customize in the editor (which is the only
 * thing that writes `overlay.lists[path]`, and Reset deletes it again), so
 * quotes already entered in the old place keep rendering untouched, and
 * switching is reversible.
 */
export function resolveAgpQuotes(sc: SiteContent, company: CompanyTestimonial[]): AgpQuote[] {
  const fromManifest = (): AgpQuote[] =>
    sc.items(QUOTES_PATH).map((r) => ({
      quote: r.quote ?? '',
      author: r.author ?? '',
      role: r.role || undefined,
      photo: r.imageKey || undefined,
    }));
  if (sc.isCustomized(QUOTES_PATH)) return fromManifest();
  if (company.length > 0)
    return company.map((t) => ({
      quote: t.quote,
      author: t.author,
      role: t.role || undefined,
      photo: t.avatarKey || undefined,
    }));
  return fromManifest();
}

export function AgpTestimonials(): JSX.Element {
  const { data } = useStorefrontContent();
  const sc = useSiteContent(AGP_SITE);
  const quotes = resolveAgpQuotes(sc, data.company.testimonials);
  // Falls back to the Home page's backdrop until this page's own image is
  // uploaded — the page borrowed `home.cta.image` before it had a field of its
  // own, and defaulting to the stock placeholder would visibly regress a site
  // whose admin had already replaced the Home one.
  const ctaBg = sc.isCustomized('testimonials.cta.image')
    ? sc.img('testimonials.cta.image')
    : sc.img('home.cta.image');

  return (
    <AgpShell active="testimonials">
      <div className="agp-page-hero">
        <div className="agp-label">{sc.text('testimonials.hero.label')}</div>
        <div className="agp-rule centered" />
        <h1>{renderLines(sc.text('testimonials.hero.title'))}</h1>
        <p>{sc.text('testimonials.hero.sub')}</p>
      </div>

      <section className="agp-section warm" style={{ textAlign: 'center' }}>
        <div className="agp-label">{sc.text('testimonials.intro.label')}</div>
        <div className="agp-rule centered" />
        <h2 className="agp-title" style={{ marginBottom: 12 }}>
          {sc.text('testimonials.intro.title')}
        </h2>
        <p className="agp-lead" style={{ maxWidth: 560, margin: '0 auto 60px' }}>
          {sc.text('testimonials.intro.lead')}
        </p>

        <div className="agp-testimonials">
          {/* Keyed by index: names repeat once the list is admin-edited. */}
          {quotes.map((t, i) => (
            <div key={i} className="agp-testimonial">
              <p className="agp-quote">{t.quote}</p>
              <div className="agp-quote-foot">
                {t.photo && <img className="agp-quote-avatar" src={sc.src(t.photo)} alt="" />}
                <div>
                  <div className="agp-quote-author">{t.author}</div>
                  {t.role && <div className="agp-quote-role">{t.role}</div>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="agp-cta">
        <div className="agp-cta-bg" style={{ backgroundImage: `url('${ctaBg}')` }} />
        <div className="agp-label">{sc.text('testimonials.cta.label')}</div>
        <h2 className="agp-cta-title">{sc.text('testimonials.cta.title')}</h2>
        <p className="agp-cta-sub">{sc.text('testimonials.cta.sub')}</p>
        <div className="agp-actions">
          <Link className="agp-btn agp-btn-primary" to="/signup">
            {sc.text('testimonials.cta.ctaPrimary')}
          </Link>
          <Link className="agp-btn agp-btn-outline-light" to="/storefront">
            {sc.text('testimonials.cta.ctaSecondary')}
          </Link>
        </div>
      </section>
    </AgpShell>
  );
}
